<template>
	<div class="border-connectProBlue border-2 pl-8 pr-8 pb-8 pt-4 rounded min-h-72">
		<div class="flex space-x-16 border-b-2 pb-5">
			<div :class="{ active: activeTab === 'customer' }" class="tab" @click="changeTab('customer')">
				{{ $t('customer') }}
			</div>
			<div :class="{ active: activeTab === 'damageassessor' }" class="tab" @click="changeTab('damageassessor')">
				{{ $t('damage_assessor') }}
			</div>
		</div>

		<DamageAssessorCaseDocumentCardTab
			v-if="activeTab === 'customer'"
			:documents="customerDocuments"
			:is-editable="isEditable"
			@upload="uploadDocument"
			@view="viewDocument"
		/>

		<DamageAssessorCaseDocumentCardTab
			v-if="activeTab === 'damageassessor'"
			:documents="damageAssessorDocuments"
			:is-editable="isEditable"
			@upload="uploadDocument"
			@view="viewDocument"
		/>

		<ImageRotationModal ref="ImageRotationModal" :title="$t('edit_image_hint').toString()" :order="1" />
	</div>
</template>

<script>
import DamageAssessorCaseDocumentCardTab from '@/components/damageassessorcases/documents/DamageAssessorCaseDocumentCardTab.vue';
import DamageAssessorCaseRepository from '@/api/repositories/DamageAssessorCaseRepository';
import { openBase64Pdf } from '@/helpers/PdfViewer';
import ImageRotationModal from '@/components/damageassessorcases/modals/ImageRotationModal.vue';

export default {
	name: 'DamageAssessorCaseDocumentUploadTab',

	components: {
		ImageRotationModal,
		DamageAssessorCaseDocumentCardTab,
	},

	props: {
		damageAssessorCase: {
			type: Object,
			required: true,
		},
		isEditable: {
			type: Boolean,
			default: true,
		},
		activeTab: {
			type: String,
			default: 'customer',
		},
	},

	data() {
		return {
			tab: 'customer',
			isLoading: false,
			pendingImageUpload: null,
			pendingImageDocument: null,
		};
	},

	computed: {
		media() {
			return this.damageAssessorCase?.media ?? [];
		},

		accidentReport() {
			return this.media.find(item => item.category === 'accident_report') ?? this.accidentReportBrokerCase ?? null;
		},

		vehicleRegistrationDocument() {
			return this.media.find(item => item.category === 'vehicle_registration_document') ?? this.vehicleRegistrationDocumentBrokerCase ?? null;
		},

		expertise() {
			return this.media.find(item => item.category === 'gutachten') ?? null;
		},

		mediaBrokerCase() {
			return this.damageAssessorCase?.broker_case?.media ?? [];
		},

		accidentReportBrokerCase() {
			return this.mediaBrokerCase.find(item => item.category === 'accident_report') ?? null;
		},

		vehicleRegistrationDocumentBrokerCase() {
			return this.mediaBrokerCase.find(item => item.category === 'vehicle_registration_document') ?? null;
		},

		customerDocuments() {
			return [
				{
					key: 'accident_report',
					title: this.$t('accident_document').toString(),
					media: this.accidentReport,
					accept: 'image/png, image/jpeg, application/pdf',
					canUpload: this.isEditable,
				},
				{
					key: 'vehicle_registration_document',
					title: this.$t('vehicle_registration').toString(),
					media: this.vehicleRegistrationDocument,
					accept: 'image/png, image/jpeg, application/pdf',
					canUpload: this.isEditable,
				},
			];
		},

		damageAssessorDocuments() {
			return [
				{
					key: 'gutachten',
					title: this.$t('expertise').toString(),
					media: this.expertise,
					accept: 'application/pdf',
					canUpload: this.isEditable && !!this.damageAssessorCase?.examined_at,
					disabledMessage:
						this.isEditable && !this.damageAssessorCase?.examined_at ? 'Das Gutachten kann nach erfolgter Begutachtung hochgeladen werden.' : null,
				},
			];
		},
	},

	methods: {
		uploadDocument({ document, base64Content }) {
			if (this.isLoading || !document?.key || !base64Content || !document.canUpload) {
				return;
			}

			const mimeType = this.getMimeType(base64Content);

			if (
				(document.key === 'accident_report' || document.key === 'vehicle_registration_document') &&
				(mimeType === 'image/jpeg' || mimeType === 'image/png')
			) {
				this.$refs.ImageRotationModal.show(base64Content).then(rotatedImage => {
					if (!rotatedImage) {
						return;
					}

					this.submitDocument(document, rotatedImage);
				});

				return;
			}

			this.submitDocument(document, base64Content);
		},
		changeTab(tab) {
			if (tab === this.activeTab) {
				return;
			}
			this.$emit('changeDocumentTab', tab);
		},
		submitDocument(document, base64Content) {
			const media = this.createMediaDto(base64Content);

			const payload = {
				[document.key]: media,
			};

			this.isLoading = true;

			new DamageAssessorCaseRepository()
				.updateDamageAssessorCase(this.damageAssessorCase.id, payload)
				.then(() => {
					this.$emit('updated');

					return this.$swal(this.$t('general.success').toString(), this.$t('general.changes_saved').toString(), 'success');
				})
				.catch(error => {
					console.log(error);

					this.showError();
				})
				.finally(() => {
					this.isLoading = false;
				});
		},
		viewDocument(document) {
			if (this.isLoading || !document?.media) {
				return;
			}

			if (document.key === 'gutachten' && !document.media.base64content) {
				this.loadAndViewExpertise(document);

				return;
			}

			this.openDocument(document.media, document.title);
		},
		loadAndViewExpertise(document) {
			this.isLoading = true;

			new DamageAssessorCaseRepository()
				.getExpertiseDocument(this.damageAssessorCase.id)
				.then(response => {
					if (!response.data?.base64content) {
						this.showError();

						return;
					}

					this.openDocument(response.data, document.title);
				})
				.catch(error => {
					console.log(error);

					this.showError();
				})
				.finally(() => {
					this.isLoading = false;
				});
		},
		openDocument(media, fallbackName) {
			if (!media?.base64content) {
				this.showError();

				return;
			}

			const extension = (media.extension ?? 'pdf').toLowerCase();
			const fileName = media.name ?? media.file_name ?? `${fallbackName}.${extension}`;

			if (extension === 'pdf') {
				openBase64Pdf(media.base64content, fileName);
				return;
			}

			if (extension === 'jpg' || extension === 'jpeg' || extension === 'png') {
				const imageWindow = window.open();
				if (!imageWindow) {
					return;
				}

				imageWindow.document.write(
					`
						<!DOCTYPE html>
						<html>
							<head>
								<title>${fileName}</title>
								<style>
									html, body {
										margin: 0;
										width: 100%;
										height: 100%;
										background: #111;
									}

									body {
										display: flex;
										justify-content: center;
										align-items: center;
									}

									img {
										max-width: 100%;
										max-height: 100%;
										object-fit: contain;
									}
								</style>
							</head>

							<body>
								<img src="${media.base64content}" alt="${fileName}" />
							</body>
						</html>
					`,
				);

				imageWindow.document.close();
			}
		},

		createMediaDto(base64Content) {
			const mimeType = this.getMimeType(base64Content);

			switch (mimeType) {
				case 'image/jpeg':
					return {
						type: 'image',
						extension: 'jpg',
						base64content: base64Content,
					};

				case 'image/png':
					return {
						type: 'image',
						extension: 'png',
						base64content: base64Content,
					};

				case 'application/pdf':
				default:
					return {
						type: 'document',
						extension: 'pdf',
						base64content: base64Content,
					};
			}
		},

		getMimeType(base64Content) {
			if (typeof base64Content !== 'string' || !base64Content.startsWith('data:')) {
				return '';
			}

			const endIndex = base64Content.indexOf(';');

			if (endIndex === -1) {
				return '';
			}

			return base64Content.substring(5, endIndex);
		},

		showError() {
			this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
		},

		confirmImageUpload(base64Content) {
			const document = this.pendingImageDocument;

			this.clearPendingImage();

			if (!document) {
				return;
			}

			this.submitDocument(document, base64Content);
		},

		clearPendingImage() {
			this.pendingImageUpload = null;
			this.pendingImageDocument = null;
		},
	},
};
</script>

<style scoped>
.tab {
	@apply cursor-pointer text-lg text-gray-500 font-bold;
}

.tab.active {
	@apply relative text-black;
}

.tab.active:after {
	content: '';
	height: 0.1rem;

	@apply absolute left-0 right-0 bottom-0 -mb-1 bg-connectProBlue;
}
</style>
